// Окно «Протокол в системе» (014: T029; сценарий 7; FR-032, FR-033; SC-006): протокол без файла
// сразу в системе — «Создать ▾ → Протокол в системе» и кнопка на «Документах» (documents.create).
// Окно «Табеля» по образцу «Нового протокола»: название (обязательно, 1–200 знаков, в одну
// строку) и ведущий по желанию — любой действующий пользователь (lead.tsx). «Создать протокол» —
// minutes.create; сервер создаёт П-N в режиме ведения с одной пустой строкой и единой формой
// колонок, ведущему выдаёт роль «Ведущий» с областью протокола. После создания — тост и сразу
// таблица протокола: от «Создать» до таблицы — четыре действия. Проверка — при отправке, ошибка
// под полем, фокус — в первое поле с ошибкой, введённое не стирается; после первой отправки поле
// перепроверяется при вводе. Отказ сервера по полю (REC-007, MIN-010 — ведущий не найден или
// отключён) — текст под полем и выноска с кодом и номером операции; остальное — тост
// с «Повторить».
// 023 (spec, сценарий 7; FR-009): без ведущего ведущим становится создатель, если ему можно быть
// ведущим, — пока ведущий не выбран, под полем «Не выбран — ведущим станете вы» (lead.tsx).
import { CreateProtocolBodySchema, PROTOCOL_TITLE_MAX } from '@kp/contracts';
import { Button, Modal, ModalFooter, TextField, useToast } from '@kp/ui';
import type { SearchPickerOption } from '@kp/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, isApiError } from '../../api/client.ts';
import { FieldsRefusalCallout } from '../../app/FieldsRefusal.tsx';
import { errorView, hasFieldTexts, useErrorToast } from '../../app/errors.ts';
import type { FieldTexts, FieldsRefusal } from '../../app/errors.ts';
import type { CreateModalProps } from '../../shell/context.tsx';
import { useMe } from '../../shell/session.ts';
import { LeadSearchField, SELF_LEAD_HINT, canLeadSelf } from './lead.tsx';

type FieldName = 'title' | 'lead';
const FIELD_ORDER: readonly FieldName[] = ['title', 'lead'];

export const TITLE_REQUIRED = 'Введите название протокола, например «Оперативное совещание»';
export const TITLE_ONE_LINE = 'Название — в одну строку, без управляющих знаков';

/** Ошибка названия по схеме запроса; нет — undefined. */
export function titleError(title: string): string | undefined {
  if (title.trim() === '') return TITLE_REQUIRED;
  return CreateProtocolBodySchema.shape.title.safeParse(title.trim()).success
    ? undefined
    : TITLE_ONE_LINE;
}

/** Поле ответа сервера → поле окна. */
function fieldOf(key: string): FieldName | undefined {
  if (key === 'title') return 'title';
  if (key === 'leadUserId') return 'lead';
  return undefined;
}

export function CreateProtocolModal({
  open,
  onOpenChange,
  returnFocusRef,
  folderId,
}: CreateModalProps & { folderId?: string | null }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const showError = useErrorToast();
  const me = useMe();
  const [title, setTitle] = useState('');
  const [lead, setLead] = useState<SearchPickerOption | null>(null);
  const [errors, setErrors] = useState<FieldTexts<FieldName>>({});
  /** Отказ сервера по полям: код и номер операции — в выноске над полями. */
  const [refusal, setRefusal] = useState<FieldsRefusal<FieldName> | null>(null);
  /** Форму уже отправляли: дальше название перепроверяется при вводе. */
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const leadRef = useRef<HTMLButtonElement>(null);

  const showErrors = (found: FieldTexts<FieldName>) => {
    setErrors(found);
    const first = FIELD_ORDER.find((field) => found[field]);
    if (first === 'title') titleRef.current?.focus();
    else if (first === 'lead') leadRef.current?.focus();
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending) return;
    setRefusal(null);
    setSubmitted(true);
    const found = titleError(title);
    if (found) {
      showErrors({ title: found });
      return;
    }
    setPending(true);
    try {
      const created = await api('minutes.create', {
        body: {
          title: title.trim(),
          ...(lead ? { leadUserId: lead.value } : {}),
          ...(folderId !== undefined ? { folderId } : {}),
        },
      });
      void queryClient.invalidateQueries({ queryKey: ['api', 'documents.list'] });
      void queryClient.invalidateQueries({ queryKey: ['api', 'manager.list'] });
      // 023 (T039): новый протокол — в сводке доступа «Документов» (колонка «Доступ», фильтры).
      void queryClient.invalidateQueries({ queryKey: ['api', 'minutes.access.summary'] });
      toast.ok(`Протокол П-${created.number} создан`);
      onOpenChange(false);
      void navigate({ to: '/protocols/$id', params: { id: created.documentId } });
    } catch (error) {
      const refuse = (fields: FieldTexts<FieldName>) => {
        showErrors(fields);
        setRefusal({ fields, view: errorView(error) });
      };
      const fields: FieldTexts<FieldName> = {};
      if (isApiError(error, 'REC-007')) {
        for (const [key, text] of Object.entries(error.fields)) {
          const field = fieldOf(key);
          if (field && !fields[field]) fields[field] = text;
        }
      }
      if (hasFieldTexts(fields)) refuse(fields);
      else if (isApiError(error, 'MIN-010')) refuse({ lead: error.message });
      else showError(error, () => void submit());
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Протокол в системе"
      description="Таблица протокола без файла Excel: пункты ведутся сразу в системе"
      returnFocusRef={returnFocusRef}
      onSubmitShortcut={() => void submit()}
      footer={
        <ModalFooter
          submitHint
          primary={
            <Button type="submit" form="kp-create-protocol" variant="primary" loading={pending}>
              Создать протокол
            </Button>
          }
        />
      }
    >
      <form
        id="kp-create-protocol"
        className="t-col kp-form"
        noValidate
        onSubmit={(event) => void submit(event)}
      >
        <FieldsRefusalCallout refusal={refusal} errors={errors} />
        <TextField
          ref={titleRef}
          label="Название"
          placeholder="например, Оперативное совещание по корпусу 2"
          autoComplete="off"
          required
          maxLength={PROTOCOL_TITLE_MAX}
          value={title}
          error={errors.title}
          onChange={(event) => {
            setTitle(event.target.value);
            if (submitted)
              setErrors((current) => ({ ...current, title: titleError(event.target.value) }));
          }}
        />
        <LeadSearchField
          ref={leadRef}
          value={lead}
          error={errors.lead}
          hint={!lead && canLeadSelf(me) ? SELF_LEAD_HINT : undefined}
          onChange={(option) => {
            setLead(option);
            setErrors((current) => ({ ...current, lead: undefined }));
          }}
        />
      </form>
    </Modal>
  );
}
